/* =====================================================================
   BOSSA // SYSTEM_COMPROMISED — PROTOCOL_06
   breach.js — runtime behaviors (only when body.breach-mode)
   - Theme/tweaks application from theme settings
   - Live UTC time + countdown in announcement bar
   - Aggressive glitch effect on headlines + section titles
   - Auto-inject SKU labels + status flags on product cards
   - Scramble effect on prices when entering viewport
   - Live transmission strip auto-typing tactical messages
   - Footer signature strip
   - Konami + DevTools easter egg
   ===================================================================== */

(function () {
  'use strict';
  const cfg = window.__BX_CONFIG__ || {};
  if (!cfg.breach_enabled) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const pad = (n) => String(n).padStart(2, '0');
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => r.querySelectorAll(s);

  // -------- APPLY THEME / TWEAKS --------
  function applyTweaks() {
    const r = document.documentElement.style;
    if (cfg.accent) r.setProperty('--bx-accent', cfg.accent);
    if (cfg.accent_2) r.setProperty('--bx-accent-2', cfg.accent_2);
    if (cfg.warn) r.setProperty('--bx-warn', cfg.warn);
    if (cfg.scanline_opacity != null) {
      r.setProperty('--bx-scanline-opacity', String(cfg.scanline_opacity / 100));
    }
    if (cfg.grid_size) r.setProperty('--bx-grid-size', `${cfg.grid_size}px`);
    if (cfg.show_grid === false) document.body.classList.add('bx-no-grid');
    if (cfg.show_scanlines === false) document.body.classList.add('bx-no-scan');
    if (cfg.discount_tag && !document.getElementById('bx-tag-style')) {
      const st = document.createElement('style');
      st.id = 'bx-tag-style';
      st.textContent =
        '.bx-tag-discount{position:absolute!important;top:8px!important;right:8px!important;left:auto!important;bottom:auto!important;z-index:4!important;width:clamp(64px,30%,130px)!important;max-width:130px!important;height:auto!important;margin:0!important;padding:0!important;border:0!important;border-radius:0!important;box-shadow:none!important;object-fit:contain!important;pointer-events:none!important}' +
        '.bx-tag-discount--pdp{top:16px!important;right:16px!important;width:clamp(110px,22%,200px)!important;max-width:200px!important}' +
        /* la imagen subida reemplaza el sello "-25% HACKED_" que pinta breach.css */
        'body.breach-mode .bossa-bs-img-wrap::before,' +
        'body.breach-mode product-card .resource-card__media::after,' +
        'body.breach-mode .product-card .resource-card__media::after,' +
        'body.breach-mode .resource-card[data-resource-type="product"] .resource-card__media::after,' +
        'body.breach-mode .product-card .card-gallery::after,' +
        'body.breach-mode product-card .card-gallery::after{content:none!important;display:none!important}' +
        'body.breach-mode .product-badges{display:none!important}';
      document.head.appendChild(st);
    }
  }

  // -------- SESSION ID --------
  let sessionId = sessionStorage.getItem('bx_session_id');
  if (!sessionId) {
    sessionId = '0x' + Math.random().toString(16).slice(2, 6).toUpperCase();
    sessionStorage.setItem('bx_session_id', sessionId);
  }

  // -------- COUNTDOWN HELPERS --------
  function diffParts(targetMs) {
    const diff = Math.max(0, targetMs - Date.now());
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    return { h, m, s, total: diff };
  }
  function getTargetMs() {
    const iso = cfg.rollback_iso || '2026-05-10T23:59:59';
    const t = new Date(iso).getTime();
    return isNaN(t) ? Date.now() + 5 * 3600 * 1000 : t;
  }

  // -------- ENRICH ANNOUNCEMENT BAR --------
  function enrichAnnouncementBar() {
    const bar = $('.announcement-bar');
    if (!bar || bar.querySelector('.bx-ann-meta')) return;

    const target = getTargetMs();
    const meta = document.createElement('span');
    meta.className = 'bx-ann-meta';
    meta.style.cssText = 'margin-left:auto;display:inline-flex;gap:12px;align-items:center;font-weight:700;letter-spacing:0.14em;color:#000;flex-shrink:0;font-size:10.5px;';
    meta.innerHTML = `
      <span class="bx-ann-utc">UTC --:--:--</span>
      <span style="opacity:.4">|</span>
      <span>REVERTS_IN <span class="bx-ann-cd">--:--:--</span></span>
      <span style="opacity:.4">|</span>
      <span>● NODE_MX_01</span>
    `;
    bar.appendChild(meta);

    const utc = meta.querySelector('.bx-ann-utc');
    const cd = meta.querySelector('.bx-ann-cd');
    const tick = () => {
      const now = new Date();
      utc.textContent = `UTC ${pad(now.getUTCHours())}:${pad(now.getUTCMinutes())}:${pad(now.getUTCSeconds())}`;
      const { h, m, s } = diffParts(target);
      cd.textContent = `${pad(h)}:${pad(m)}:${pad(s)}`;
    };
    tick();
    setInterval(tick, 1000);
  }

  // -------- GLITCH ON HEADLINES (perf-friendly: only animates elements in viewport) --------
  function initGlitch() {
    const intensity = (cfg.glitch_intensity != null ? cfg.glitch_intensity : 90) / 100;
    if (!intensity || reduceMotion) return;

    const selectors = [
      '.wrapper-text-hero h3',
      '.bossa-bs-header h2',
      '.bossa-exp__title',
      '.benefit-slider-header h2',
      '.benefit-slider-header h3',
      '.colecciones-tabs-title',
      '.wrapper-title-sucursal h2',
      '.hero-collection-women__title',
      '.container-text-garantia h2',
      '.container-text-garantia h3',
      '.wrapper-text-garantia h2',
    ];
    const targets = [];
    selectors.forEach((sel) => $$(sel).forEach((el) => targets.push(el)));
    if (!targets.length) return;

    const chars = '▓▒░█▌▐■◣◤/\\#@$%&*';
    const visibleSet = new WeakSet();
    let docVisible = !document.hidden;
    document.addEventListener('visibilitychange', () => { docVisible = !document.hidden; });

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) visibleSet.add(e.target);
          else visibleSet.delete(e.target);
        });
      }, { rootMargin: '100px' });
      targets.forEach((el) => io.observe(el));
    } else {
      targets.forEach((el) => visibleSet.add(el));
    }

    targets.forEach((el) => {
      const textNode = findFirstTextNode(el);
      if (!textNode) return;
      const realText = textNode.nodeValue;

      const tick = () => {
        if (docVisible && visibleSet.has(el) && Math.random() < intensity) {
          const arr = realText.split('');
          const swaps = Math.max(1, Math.floor(Math.random() * 4));
          for (let i = 0; i < swaps; i++) {
            const idx = Math.floor(Math.random() * arr.length);
            if (arr[idx] !== ' ' && arr[idx] !== '\n') {
              arr[idx] = chars[Math.floor(Math.random() * chars.length)];
            }
          }
          textNode.nodeValue = arr.join('');
          setTimeout(() => { textNode.nodeValue = realText; }, 70 + Math.random() * 120);
        }
        const baseDelay = 1400 - 700 * intensity;
        setTimeout(tick, baseDelay + Math.random() * 1400);
      };
      setTimeout(tick, 600 + Math.random() * 1200);
    });
  }

  function findFirstTextNode(el) {
    for (const n of el.childNodes) {
      if (n.nodeType === Node.TEXT_NODE && n.nodeValue.trim()) return n;
    }
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    let n;
    while ((n = walker.nextNode())) {
      if (n.nodeValue.trim()) return n;
    }
    return null;
  }

  // -------- TAG DE DESCUENTO EN LA FICHA DE PRODUCTO --------
  // El tema muestra el precio REAL de Shopify (con compare-at si existe).
  // El breach NO inventa precios: el descuento lo aplica un codigo real.
  function decorateProductPage() {
    if (cfg.template_name !== 'product' || !cfg.discount_tag) return;
    const media = document.querySelector('media-gallery');
    if (!media || media.querySelector('.bx-tag-discount')) return;
    if (getComputedStyle(media).position === 'static') media.style.position = 'relative';
    const img = document.createElement('img');
    img.className = 'bx-tag-discount bx-tag-discount--pdp';
    img.src = cfg.discount_tag;
    img.alt = '';
    img.setAttribute('aria-hidden', 'true');
    media.appendChild(img);
  }

  function generateSku(title, idx) {
    const id = String(idx + 1).padStart(2, '0');
    if (!title) return `SKU_BSA_${id}`;
    const word = title.toUpperCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/[^A-Z0-9 ]/g, '')
      .trim().split(/\s+/)[0]
      .slice(0, 5);
    return `SKU_${word}_${id}`;
  }

  function decorateProductCards() {
    const cards = $$('.bossa-bs-card, .coleccion-product-card, product-card, .product-card, .resource-card[data-resource-type="product"], .product-grid__item');
    cards.forEach((card, i) => {
      if (card.dataset.bxDecorated === '1') return;
      card.dataset.bxDecorated = '1';

      const titleEl = card.querySelector(
        '.bossa-bs-title, .collection-title-price h4, .resource-card__title, ' +
        '.product-grid-view-zoom-out--details h3, ' +
        '.text-block.h4, .text-block.h3, .text-block.h5, ' +
        '.product-card .text-block, ' +
        'h3.h4, h2.h4, h3.h3, h2.h3, .product-card h3, .product-card h2'
      );
      const title = titleEl ? titleEl.textContent.trim() : '';
      const sku = generateSku(title, i);

      const isSmallCtx = !!card.closest(
        '.search-modal, search-modal, .predictive-search, predictive-search, ' +
        '.search-modal__body, dialog[id*="search" i], [class*="search-modal"], ' +
        '.recently-viewed, [data-recently-viewed], recently-viewed-products'
      );
      if (isSmallCtx) return;

      const imgWrap = card.querySelector(
        '.bossa-bs-img-wrap, .coleccion-product-image, .resource-card__media, ' +
        '.card-gallery, ' +
        '.product-card__image-wrapper, .product-card__media'
      );
      if (imgWrap) {
        const cs = window.getComputedStyle(imgWrap);
        if (cs.position === 'static') imgWrap.style.position = 'relative';

        if (!imgWrap.querySelector('.bx-tag-sku')) {
          const skuEl = document.createElement('span');
          skuEl.className = 'bx-tag-sku';
          skuEl.textContent = sku;
          imgWrap.appendChild(skuEl);
        }

        if (!imgWrap.querySelector('.bx-tag-flag')) {
          const flags = ['HOT', 'NEW', 'LAST', 'ENCRYPTED'];
          const flag = flags[i % flags.length];
          const flagEl = document.createElement('span');
          flagEl.className = `bx-tag-flag bx-flag-${flag.toLowerCase()}`;
          flagEl.textContent = flag;
          imgWrap.appendChild(flagEl);
        }
      }

      if (cfg.discount_tag && imgWrap && !imgWrap.querySelector('.bx-tag-discount')) {
        const tagImg = document.createElement('img');
        tagImg.className = 'bx-tag-discount';
        tagImg.src = cfg.discount_tag;
        tagImg.alt = '';
        tagImg.setAttribute('aria-hidden', 'true');
        imgWrap.appendChild(tagImg);
      }
    });
  }

  // -------- SCRAMBLE NUMBERS ON VIEWPORT ENTRY --------
  function initScrambleObserver() {
    if (!('IntersectionObserver' in window) || reduceMotion) return;

    const targets = $$('.bossa-bs-price .current, .coleccion-product-card .price, .bossa-exp__label, .resource-card__content .price, product-card .price, .product-card .price');
    if (!targets.length) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.target.dataset.bxScrambled === '1') return;
        entry.target.dataset.bxScrambled = '1';
        scrambleText(entry.target, 700);
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.6 });

    targets.forEach((el) => observer.observe(el));
  }

  function scrambleText(el, durationMs) {
    const original = el.textContent;
    if (!original.trim()) return;
    el.classList.add('bx-scrambling');
    const chars = '0123456789ABCDEF$,.';
    const start = performance.now();
    const tick = () => {
      const elapsed = performance.now() - start;
      if (elapsed >= durationMs) {
        el.textContent = original;
        el.classList.remove('bx-scrambling');
        return;
      }
      const reveal = Math.floor((elapsed / durationMs) * original.length);
      const out = original.split('').map((c, i) => {
        if (i < reveal) return c;
        if (c === ' ' || c === '$' || c === ',' || c === '.') return c;
        return chars[Math.floor(Math.random() * chars.length)];
      }).join('');
      el.textContent = out;
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  // -------- HOVER SCRAMBLE ON PRODUCT TITLES --------
  function initHoverScramble() {
    if (reduceMotion) return;
    $$('.bossa-bs-title, .collection-title-price h4, .resource-card__title, product-card .resource-card__title, product-card .text-block, .product-card .text-block, product-card h3.h4, .product-card h3.h4').forEach((el) => {
      let busy = false;
      el.addEventListener('mouseenter', () => {
        if (busy) return;
        busy = true;
        scrambleText(el, 400);
        setTimeout(() => { busy = false; }, 500);
      });
    });
  }

  // -------- FOOTER SIGNATURE STRIP --------
  function injectFooterStrip() {
    const footer = $('footer, .footer');
    if (!footer || $('.bx-footer-strip')) return;
    const strip = document.createElement('div');
    strip.className = 'bx-footer-strip';
    const words = ['TRUST', 'INTEGRITY', 'CRAFT', 'LEATHER', 'CDMX', '2018→', 'SIGNED', `SERIAL_${sessionId}`];
    strip.innerHTML = words.map((w) => `<span>${w}</span>`).join('');
    footer.insertBefore(strip, footer.firstChild);
  }

  // -------- PRICE GLITCH --------
  function initPriceGlitch() {
    const SEL = [
      '.price-item--sale',
      '.price__last',
      '.bossa-bs-price .current',
      '.product-card .price',
      'product-card .price',
      '.resource-card__content .price',
      '.collection-title-price .price'
    ].join(',');
    const SYMBOLS = '▓▒░█▌▐■◣◤/\\#@$%&*';
    const DIGIT_RE = /\d/g;
    let lastTarget = null;

    function pickAndGlitch() {
      const candidates = Array.from(document.querySelectorAll(SEL))
        .filter((el) => {
          if (!el.offsetParent) return false;
          if (el === lastTarget) return false;
          const txt = (el.textContent || '').trim();
          return txt && /\d/.test(txt);
        });
      if (!candidates.length) return scheduleNext();

      const target = candidates[Math.floor(Math.random() * candidates.length)];
      lastTarget = target;

      const original = target.textContent;
      const scrambled = original.replace(DIGIT_RE, () => SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)]);

      target.classList.add('bx-price-glitch');
      target.textContent = scrambled;

      setTimeout(() => {
        if (target.isConnected) target.textContent = original;
        setTimeout(() => target.classList.remove('bx-price-glitch'), 120);
      }, 110);

      scheduleNext();
    }

    function scheduleNext() {
      const delay = 1800 + Math.random() * 2800;
      setTimeout(pickAndGlitch, delay);
    }

    if (reduceMotion) return;
    scheduleNext();
  }

  // -------- DEVTOOLS CONSOLE --------
  function consoleEasterEgg() {
    if (window.__bx_console_done) return;
    window.__bx_console_done = true;
    console.log('%c> BOSSA // SYSTEM_COMPROMISED — PROTOCOL_06', 'color:#00FF41;font:bold 16px monospace;');
    console.log('%c> /dev/null_collective', 'color:#B6FF6B;font:14px monospace;');
    console.log('%c> Welcome, operator.', 'color:#B6FF6B;font:14px monospace;');
    console.log('%c> Backdoor code: BACKDOOR40 — additional override at checkout.', 'color:#00FF41;font:14px monospace;');
  }

  // -------- KONAMI CODE --------
  function initKonami() {
    const seq = ['ArrowUp','ArrowUp','ArrowDown','ArrowDown','ArrowLeft','ArrowRight','ArrowLeft','ArrowRight','b','a'];
    let i = 0;
    document.addEventListener('keydown', (e) => {
      if (e.key.toLowerCase() === seq[i].toLowerCase()) {
        i++;
        if (i === seq.length) { i = 0; flashUnlock(); }
      } else { i = 0; }
    });
  }

  function flashUnlock() {
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position:fixed;inset:0;z-index:99998;background:rgba(0,0,0,0.95);
      display:flex;align-items:center;justify-content:center;
      color:#00FF41;font-family:'Share Tech Mono','JetBrains Mono',monospace;font-size:1.1rem;
      letter-spacing:0.14em;text-align:center;padding:40px;text-shadow:0 0 4px rgba(0,255,65,0.6);
    `;
    overlay.innerHTML = `
      <div>
        <div style="font-family:'VT323','Share Tech Mono',monospace;font-size:3rem;margin-bottom:16px;text-shadow:0 0 12px #00FF41;font-weight:400;letter-spacing:0.04em;">[ BACKDOOR ACCESS GRANTED ]</div>
        <div style="color:#00FF41;margin-bottom:12px;font-weight:700;">Hidden directory unlocked.</div>
        <div style="font-size:0.9rem;color:#00B82E;">Redirecting to /collections/unlisted in 2s...</div>
      </div>
    `;
    document.body.appendChild(overlay);
    setTimeout(() => { window.location.href = '/collections/unlisted'; }, 2000);
  }

  // -------- SYSTEM_ALERT BLOCK (404, empty cart, empty search, empty collection) --------
  function buildSystemAlert(opts) {
    const o = Object.assign({
      file: 'price_database.sql',
      status: 'CONNECTION_LOST',
      retry: '06:14:23',
      code: '404_PRICES_NOT_FOUND',
      number: '404',
      subtitle: 'precios no encontrados.',
      body: 'Algo pasó con el servidor. Los precios oficiales se perdieron y solo quedaron... estos. Mientras nuestro equipo de TI lo resuelve, llévatelos así.',
      ctaText: 'EXFILTRAR_INVENTARIO',
      ctaHref: '/collections/all',
      ctaSecondaryText: '',
      ctaSecondaryHref: '',
      compact: false,
    }, opts || {});

    const wrap = document.createElement('div');
    wrap.className = 'bx-system-alert' + (o.compact ? ' bx-sa--compact' : '');
    wrap.innerHTML = `
      <div class="bx-sa-header">
        <span class="bx-sa-warn-icon">⚠</span>
        <span>SYSTEM_ALERT</span>
        <span class="bx-sa-sep">·</span>
        <span>${o.file}</span>
        <span class="bx-sa-sep">·</span>
        <span class="bx-sa-status">${o.status}</span>
      </div>
      <div class="bx-sa-retry">retry in <span class="bx-sa-time" data-bx-sa-retry>${o.retry}</span></div>
      <div class="bx-sa-code">ERROR_CODE: ${o.code}</div>
      <div class="bx-sa-number">${o.number.repeat(3).slice(0, 9)}</div>
      <p class="bx-sa-subtitle">${o.subtitle}</p>
      <p class="bx-sa-body">${o.body}</p>
      <div class="bx-sa-ctas">
        ${o.ctaText ? `<a class="bx-sa-cta" href="${o.ctaHref}">${o.ctaText}</a>` : ''}
        ${o.ctaSecondaryText ? `<a class="bx-sa-cta bx-sa-cta--ghost" href="${o.ctaSecondaryHref}">${o.ctaSecondaryText}</a>` : ''}
      </div>
    `;

    const retryEl = wrap.querySelector('[data-bx-sa-retry]');
    if (retryEl) {
      const target = getTargetMs();
      const tick = () => {
        const { h, m, s } = diffParts(target);
        retryEl.textContent = `${pad(h)}:${pad(m)}:${pad(s)}`;
      };
      tick();
      setInterval(tick, 1000);
    }
    return wrap;
  }

  function injectSystemAlerts() {
    const tpl = (cfg.template_name || '').toString();

    const main404 = $('main #shopify-section-main-404, main [class*="404"], main .main-404');
    if (main404 && tpl === '404' && !main404.querySelector('.bx-system-alert')) {
      main404.appendChild(buildSystemAlert({
        code: '404_PAGE_NOT_FOUND',
        number: '404',
        subtitle: 'esta página no existe en nuestro sistema.',
        body: '<strong>O quizás la perdimos durante el breach.</strong> El operador se llevó los enlaces. Volvé a la base — el inventario sigue ahí, comprometido y listo.',
        ctaText: 'VOLVER_A_BASE',
        ctaHref: '/',
        ctaSecondaryText: 'EXFILTRAR_INVENTARIO',
        ctaSecondaryHref: '/collections/all',
      }));
    }

    const emptyCart = $('.cart-drawer__empty, [class*="cart"][class*="empty"], main-cart [class*="empty"]');
    if (emptyCart && !emptyCart.querySelector('.bx-system-alert')) {
      emptyCart.appendChild(buildSystemAlert({
        file: 'staged_payload.sql',
        status: 'EMPTY_BUFFER',
        code: 'NO_ASSETS_STAGED',
        number: '000',
        subtitle: 'payload vacío.',
        body: 'No hay assets en cola para exfiltrar. Eligí piezas, cargalas al payload, y salí del sistema antes del rollback.',
        ctaText: 'EXFILTRAR_INVENTARIO',
        ctaHref: '/collections/all',
        compact: true,
      }));
    }

    const emptySearch = $('.search-results__empty, .predictive-search__empty, [class*="search"][class*="empty"]');
    if (emptySearch && !emptySearch.querySelector('.bx-system-alert')) {
      emptySearch.appendChild(buildSystemAlert({
        file: 'query_handler.sql',
        status: 'NULL_RESPONSE',
        code: 'QUERY_RETURNED_NULL',
        number: '000',
        subtitle: 'búsqueda sin coincidencias.',
        body: 'El operador borró las referencias. Probá con otra palabra o explorá las colecciones — todo el catálogo sigue comprometido a -25%.',
        ctaText: 'VER_INVENTARIO',
        ctaHref: '/collections/all',
        compact: true,
      }));
    }

    const emptyCol = $('.main-collection-grid__empty');
    if (emptyCol && !emptyCol.querySelector('.bx-system-alert')) {
      emptyCol.textContent = '';
      emptyCol.appendChild(buildSystemAlert({
        file: 'collection_filter.sql',
        status: 'NO_MATCHES',
        code: 'FILTER_RETURNED_EMPTY',
        number: '000',
        subtitle: 'sin assets en este filtro.',
        body: 'Ningún archivo cumple los criterios. Limpiá los filtros para ver las 248 piezas comprometidas.',
        ctaText: 'LIMPIAR_FILTROS',
        ctaHref: window.location.pathname.split('?')[0],
        ctaSecondaryText: 'VER_TODO',
        ctaSecondaryHref: '/collections/all',
      }));
    }
  }

  // -------- ACTIVATE BREACH (apply class + run all decorations) --------
  const idle = window.requestIdleCallback || ((cb) => setTimeout(() => cb({ timeRemaining: () => 0 }), 1));

  let breachActivated = false;
  function activateBreach() {
    if (breachActivated) return;
    breachActivated = true;

    if (!document.body.classList.contains('breach-mode')) {
      document.body.classList.add('breach-mode');
    }
    if (cfg.template_name) {
      document.body.classList.add('template-' + String(cfg.template_name).replace(/[^a-z0-9]/gi, '-'));
    }

    applyTweaks();
    decorateProductCards();
    decorateProductPage();
    injectSystemAlerts();

    setTimeout(() => {
      enrichAnnouncementBar();
      initGlitch();
    }, 0);

    idle(() => initScrambleObserver());
    idle(() => initHoverScramble());
    idle(() => injectFooterStrip());
    idle(() => initPriceGlitch());
    idle(() => consoleEasterEgg());
    idle(() => initKonami());

    let pending = false;
    const debounced = () => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => {
        setTimeout(() => {
          pending = false;
          decorateProductCards();
          injectSystemAlerts();
        }, 220);
      });
    };
    const target = $('main') || document.body;
    new MutationObserver(debounced).observe(target, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', activateBreach);
  } else {
    activateBreach();
  }
})();
